import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, PencilSimple, Check, X } from '@phosphor-icons/react';
import { useToast } from '@chatsdet/ui';

export type CodeTab = {
  id: string;
  label: string;
  /** Small mono badge after the label (e.g. "7 props"). */
  badge?: string;
  /** Display text for the tab. Null/empty renders the empty state. */
  content: string | null;
  /** Highlighter language. Defaults to 'json'. */
  language?: string;
  /** When set, an Edit affordance appears and saves call this. */
  onSave?: (value: string | null) => void;
  emptyText?: string;
};

export interface CodePanelProps {
  tabs: CodeTab[];
  defaultTabId?: string;
  isSaving?: boolean;
  /** Foot-strip content-type label (e.g. "application/json"). */
  contentTypeLabel?: string;
}

/**
 * Light, app-matching code viewer with segmented sub-tabs, gutter line numbers,
 * a copy tool, and a foot strip. Tabs with an `onSave` get an inline editor
 * (textarea) so editable content keeps its save behavior. Semantic tokens only
 * → renders in both brands.
 */
export const CodePanel: React.FC<CodePanelProps> = ({
  tabs,
  defaultTabId,
  isSaving = false,
  contentTypeLabel,
}) => {
  const { toast } = useToast();
  const [activeId, setActiveId] = React.useState(defaultTabId ?? tabs[0]?.id);
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  const active = tabs.find((t) => t.id === activeId) ?? tabs[0];
  if (!active) return null;

  // Leaving edit mode whenever the active tab changes.
  React.useEffect(() => {
    setIsEditing(false);
  }, [activeId]);

  const lineCount = active.content ? active.content.split('\n').length : 0;

  const handleCopy = async () => {
    if (!active.content) return;
    try {
      await navigator.clipboard.writeText(active.content);
      toast.success(`Copied ${active.label.toLowerCase()}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to copy');
    }
  };

  const startEdit = () => {
    setDraft(active.content ?? '');
    setIsEditing(true);
  };

  const saveEdit = () => {
    active.onSave?.(draft.trim() ? draft : null);
    setIsEditing(false);
  };

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden">
      {/* Segmented sub-tabs + tools */}
      <div className="flex items-center h-[34px] border-b border-border bg-muted/30 pr-1.5">
        <div className="flex h-full">
          {tabs.map((tab) => {
            const on = tab.id === active.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveId(tab.id)}
                className={`h-full px-3.5 inline-flex items-center gap-1.5 text-[12.5px] font-medium transition-colors relative ${
                  on
                    ? 'text-foreground bg-card shadow-[inset_0_-2px_0_hsl(var(--foreground))]'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
                {tab.badge && (
                  <span className="font-mono text-[10px] px-1 py-px rounded border border-border bg-muted/60 text-muted-foreground">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-0.5">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={saveEdit}
                disabled={isSaving}
                aria-label="Save"
                className="w-[26px] h-[26px] inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50"
              >
                <Check size={13} />
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                aria-label="Cancel"
                className="w-[26px] h-[26px] inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                <X size={13} />
              </button>
            </>
          ) : (
            <>
              {active.onSave && (
                <button
                  type="button"
                  onClick={startEdit}
                  aria-label="Edit"
                  className="w-[26px] h-[26px] inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <PencilSimple size={13} />
                </button>
              )}
              <button
                type="button"
                onClick={handleCopy}
                disabled={!active.content}
                aria-label="Copy"
                className="w-[26px] h-[26px] inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40"
              >
                <Copy size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      {isEditing ? (
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          spellCheck={false}
          className="w-full min-h-[180px] max-h-[420px] p-3 font-mono text-[12.5px] leading-[1.55] bg-card text-foreground outline-none resize-y"
        />
      ) : active.content ? (
        <SyntaxHighlighter
          language={active.language ?? 'json'}
          style={oneLight}
          showLineNumbers
          customStyle={{
            margin: 0,
            background: 'transparent',
            fontSize: '12.5px',
            maxHeight: '420px',
          }}
          lineNumberStyle={{ minWidth: '2.5em', color: 'hsl(var(--muted-foreground))', opacity: 0.6 }}
          codeTagProps={{ style: { fontFamily: 'var(--font-mono, monospace)' } }}
        >
          {active.content}
        </SyntaxHighlighter>
      ) : (
        <div className="px-3.5 py-6 font-mono text-[11.5px] text-muted-foreground">
          {active.emptyText ?? 'Nothing captured yet.'}
        </div>
      )}

      {/* Foot strip */}
      {!isEditing && active.content && (
        <div className="flex items-center justify-between border-t border-border bg-muted/30 px-3 py-1.5 font-mono text-[10.5px] text-muted-foreground">
          <span className="flex items-center gap-3">
            {contentTypeLabel && <span>{contentTypeLabel}</span>}
            <span>
              {lineCount} line{lineCount === 1 ? '' : 's'}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};
